import Render from '@Views/root/user/account/informations.html'
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import {IBreadcrumbItem, INotification, IPopin, IPopinAction, IUser} from '@Store/types'
import { IToggleBoxItem } from '@Components/index'
import { User, Address } from '@Models/index'
import {breadcrumb, notif, popIn, user} from '@Store/modules'
import {Gender} from "@Enums/gender";
import {Profile} from "@Enums/profile";
import {UserRfeStatus} from "@Enums/user-rfe-status";
import {isEmpty} from "lodash";

@Render
@Component({})
export class Informations extends Vue {

	private _hasAdeliError: boolean = false
	@Prop() submitted: boolean
	@Prop() user: IUser

	//#region Getters
	get canTransmit(): boolean {
		return this.user && this.user.can_transmit
	}
	get canActiveMedSmart(): boolean{
		return this.user.is_medsmart_active;
	}
	get canShowRfeFields(): boolean {
		return !!this.user && this.user.profile !== Profile.Secretary && this.user.profile !== Profile.Accounting
	}
	get hasLastNameError(): boolean {
		return this.submitted && !this.user.coordinates.last_name
	}
	get hasFirstNameError(): boolean {
		return this.submitted && !this.user.coordinates.first_name
	}
	get hasSirenError(): boolean {
		let siren: string = (this.user.coordinates.siren || '').replace(/\s/g, '')
		if (!siren) return false
		return !User.isLuhnNum(siren) && (this.submitted || siren.length >= 9)
	}
	get isLoading(): boolean {
		return user.isLoading
	}
	get isTransmitting(): boolean {
		return user.isTransmitting
	}
	get isEnterpriseLoading(): boolean {
		return user.isEnterpriseLoading
	}
	get canAutoFill(): boolean {
		return !!this.user && !!this.user.coordinates.siren && User.isLuhnNum(this.user.coordinates.siren.replace(/\s/g, ''))
	}
	get genderItems(): IToggleBoxItem[] {
		return [
			{value: Gender.Man, label: this.$i18n.t('vm.root.user.informations.gender.man').toString(), icon:"mars"},
			{value: Gender.Woman, label: this.$i18n.t('vm.root.user.informations.gender.woman').toString(), icon:"venus"},
			{value: Gender.Other, label: this.$i18n.t('vm.root.user.informations.gender.other').toString()}
		]
	}
	private get hasValidSiren(): boolean {
		return !this.user.coordinates.siren || User.isLuhnNum(this.user.coordinates.siren)
	}


	//#endregion

	@Watch('user', { immediate: true })
	private ensureCompanyAddress(): void {
		if (!this.user || !this.user.coordinates) return
		if (!this.user.coordinates.address) {
			this.$set(this.user.coordinates, 'address', Address.createDefaultAddress())
		}
	}

	private get hasCompanyAddress(): boolean {
		let address = this.user && this.user.coordinates ? this.user.coordinates.address : null
		return !!address && !!address.street && !!address.zipcode && !!address.city
	}

	checkRfeInfos(): boolean {
		return !!this.user && !!this.user.coordinates.siren && !!this.user.coordinates.naf && !!this.user.coordinates.company_name && this.hasCompanyAddress
	}

	private isTrue(value: any): boolean {
		return value === true || value === 'true' || value === 1 || value === '1'
	}

	private get isPaAlreadySelected(): boolean {
		return !!(this.user && this.user.rfe) && this.user.rfe.status === UserRfeStatus.Accepted
	}

	// Seul un titulaire disposant d'une PA active peut demander une annulation.
	get canCancelPa(): boolean {
		return !!this.user && this.user.profile === Profile.Main && this.isPaAlreadySelected
	}

	// l'utilisateur a déja refusée le PA
	private get isPaRefused(): boolean {
		return !!this.user && !this.canShowCommunication && !this.isPaAlreadySelected
	}

	get canShowCommunication(): boolean {
		return !!this.user && this.isTrue(this.user.can_show_communication_rfe)
	}
	get showRfeAlert(): boolean {
		if (!this.user || this.user.profile !== Profile.Main) return false
		// Souscrire un pack RFE nécessite une licence active.
		if (this.user.isSubscriptionRequired) return false
		return this.canShowCommunication || !this.isPaAlreadySelected
	}

	warnIfRfeInfosIncomplete(): void {
		if (!this.canShowRfeFields) return
		if (this.user.isSubscriptionRequired) return
		// Si l'utilisateur a refusé la PA, on ne le relance pas pour compléter ses informations.
		if (this.isPaRefused) return
		if (this.checkRfeInfos()) return

		let notification: INotification = {message: this.$i18n.t('vm.root.user.informations.facture-electronique.infos.text1').toString(), actions: [], delay: false, canClose: true}
		notif.warning(notification)
	}

	async mounted() {
		this.$emit('navigate', 'account')
		this.initializeBreadcrumb()
		this.warnIfRfeInfosIncomplete()

		if (this.canActiveMedSmart && !this.canTransmit) {
			await user.invokeMedSmartCallbackPs();
		}
		await this.checkMedSmartNipUpdateLog()
	}

	hasAdeliError(): boolean {
		return this._hasAdeliError
	}

	async autoFillFromSiren(): Promise<void> {
		if (!this.canAutoFill || this.isEnterpriseLoading) return

		let tva: string = User.computeTvaFromSiren(this.user.coordinates.siren)
		if (!!tva) this.$set(this.user.coordinates, 'tva', tva)

		try {
			let info = await user.fetchEnterpriseInfo(this.user.coordinates.siren)
			if (!!info && !!info.naf) this.$set(this.user.coordinates, 'naf', info.naf)

			let notification: INotification = {message: this.$i18n.t('vm.root.user.informations.facture-electronique.autofill.success').toString(), actions: [], delay: 5000, canClose: true}
			notif.success(notification)
		} catch (err) {
			let notification: INotification = {message: this.$i18n.t('vm.root.user.informations.facture-electronique.autofill.error').toString(), actions: [], delay: 5000, canClose: true}
			notif.error(notification)
		}
	}

	async checkMedSmartNipUpdateLog(): Promise<void> {
		if (this.canTransmit) {
			 const data = await user.getMedSmartNipUpdateLog()
			 if (!isEmpty(data) && parseInt(data.status) !== 200) {
				 let notification: INotification = {message: this.$i18n.t('vm.root.user.informations.transmit.message-update-nip').toString(), actions:[], delay: 10000, canClose: true}
				 notif.error(notification)
			 }
		}
	}


	async activeMedSmart(): Promise<void> {
		if (this.canTransmit) {
			let notification: INotification = {message: this.$i18n.t('vm.root.user.informations.transmit.medsmart-actived').toString(), actions:[], delay: 5000, canClose: true}
			notif.warning(notification)
			return
		} else {
			this._hasAdeliError = false
			this.$forceUpdate()
			user.activeTransmit()
				.then((url) => {
					let medSmartPopin: IPopin = {
						title: `Connexion à MedSmart`,
						content: {component: 'popin-medsmart'},
						options: {url: url},
						isWide: true,
						cancelLabel: this.$i18n.t('general.close.text').toString(),
					}

					popIn.info(medSmartPopin)
				})
				.catch((error) => {
					if (!!this.user.coordinates.adeli && this.user.coordinates.adeli.trim().length > 0) {
						notif.error(error)
					} else {
						this._hasAdeliError = true
						this.$forceUpdate()
					}
				})
		}


	}

	private initializeBreadcrumb(): void {
		let item: IBreadcrumbItem = { label: this.$i18n.t('vm.root.breadcrumb.user').toString() }
		breadcrumb.updateItems([item])
	}

	async acceptPa(): Promise<void> {
		this.communicationRfe()
	}

	private communicationRfe(): void {

		let action: IPopinAction = {
			label: this.$i18n.t('vm.root.home.communication-rfe.action.label').toString(),
			title: this.$i18n.t('vm.root.home.communication-rfe.action.title').toString(),
			condition: ({ choice, refuseReason }: { choice: string, refuseReason: string }) => {
				if (choice == "1") return true;
				if (choice == "0") return refuseReason.trim().length > 0;
				return false;
			},
			callback: async ({choice, refuseReason, substituteIds}: {user: IUser, choice: string, refuseReason: string, substituteIds: string[]}, updateLoading: (loading: boolean) => void) => {
				try {
					updateLoading(true)
					await user.updateRfeChoice({ choice, reason: refuseReason, substituteIds })
					let notification: INotification = {message: this.$i18n.t('vm.root.home.communication-rfe.message.success').toString(), actions:[], delay: 5000, canClose: true}
					notif.success(notification)
					await user.reloadUser()
				} catch (err) {
					let notification: INotification = {message: this.$i18n.t('vm.root.home.communication-rfe.message.failed').toString(), actions:[], delay: 5000, canClose: true}
					notif.error(notification)
				} finally {
					try { await user.reloadUser() } catch (e) {  }
					updateLoading(false)
				}
			},
			stayOpen: false
		}
		let communicationRfe: IPopin = {
			title: this.$i18n.t('vm.root.home.communication-rfe.title').toString(),
			content: {component: 'popin-communication-rfe'},
			action,
			isWide: true,
			isWizard: true,
			options: {
				choice: '',
				refuseReason: '',
				substituteIds: []
			},
			cancelAction: this.noChoiceAction,
			cancelLabel: this.$i18n.t('vm.root.home.communication-rfe.no-choice').toString()
		}

		popIn.info(communicationRfe)
	}

	private noChoiceAction(): void {
		this.$emit('close')
	}

	cancelPa(): void {
		if (!this.canCancelPa) return
		this.cancelRfe()
	}

	private cancelRfe(): void {
		let action: IPopinAction = {
			label: this.$i18n.t('vm.components.popin.cancel-rfe.confirm').toString(),
			title: this.$i18n.t('vm.components.popin.cancel-rfe.confirm').toString(),
			condition: ({ choice, reason, substituteIds }: { choice: string, reason: string, substituteIds: string[] }) => {
				// titulaire : motif obligatoire.
				// remplaçants : au moins une sélection de remplaçant
				if (choice === 'principal') return reason.trim().length > 0
				if (choice === 'substitutes') return substituteIds.length > 0
				return false
			},
			callback: async ({ reason, substituteIds }: { choice: string, reason: string, substituteIds: string[] }, updateLoading: (loading: boolean) => void) => {
				try {
					updateLoading(true)
					await user.cancelRfeChoice({ reason, substituteIds })
					let notification: INotification = {message: this.$i18n.t('vm.components.popin.cancel-rfe.success').toString(), actions:[], delay: 5000, canClose: true}
					notif.success(notification)
				} catch (err) {
					let notification: INotification = {message: this.$i18n.t('vm.components.popin.cancel-rfe.failed').toString(), actions:[], delay: 5000, canClose: true}
					notif.error(notification)
				} finally {
					try { await user.reloadUser() } catch (e) {  }
					updateLoading(false)
				}
			},
			stayOpen: false
		}
		let cancelRfe: IPopin = {
			title: this.$i18n.t('vm.components.popin.cancel-rfe.title').toString(),
			content: {component: 'popin-cancel-rfe'},
			action,
			isWide: true,
			isWizard: true,
			options: {
				choice: '',
				reason: '',
				substituteIds: []
			}
		}

		popIn.info(cancelRfe)
	}

}
